'use client';
import { useEffect, useState } from 'react';
import type { Birthday } from '@/lib/types';
import { useBirthdays } from '@/hooks/use-birthdays';
import { suggestGifts } from '@/ai/flows/gift-suggestion-from-birthday-data';
import { calculateAge } from '@/lib/date-utils';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Wand2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';

type GiftSuggestionProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  birthday: Birthday;
};

export function GiftSuggestion({
  isOpen,
  setIsOpen,
  birthday,
}: GiftSuggestionProps) {
  const { getGiftSuggestion, setGiftSuggestion } = useBirthdays();
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      const fetchSuggestions = async () => {
        setIsLoading(true);
        setError(null);
        
        if (!birthday.dob) {
          setError('A date of birth is required to suggest gifts.');
          setIsLoading(false);
          return;
        }

        // Check cache first
        const cached = getGiftSuggestion(birthday.id);
        if (cached) {
          setSuggestions(cached);
          setIsLoading(false);
          return;
        }

        try {
          const age = calculateAge(birthday.dob);
          if (age === null) {
            setError('Could not calculate age from the provided date of birth.');
            setIsLoading(false);
            return;
          }

          const result = await suggestGifts({
            age: age,
            relation: birthday.relation,
            notes: birthday.notes || 'No notes provided.',
          });
          
          if (result && result.giftIdeas) {
            setSuggestions(result.giftIdeas);
            setGiftSuggestion(birthday.id, result.giftIdeas); // Cache the result
          } else {
             setError('Could not generate gift ideas. The AI returned an unexpected response.');
          }
        } catch (e) {
          console.error(e);
          setError('An error occurred while fetching gift ideas. Please try again later.');
        } finally {
          setIsLoading(false);
        }
      };

      fetchSuggestions();
    }
  }, [isOpen, birthday, getGiftSuggestion, setGiftSuggestion]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="text-primary" />
            Gift Ideas for {birthday.name}
          </DialogTitle>
          <DialogDescription>
            Based on their age, relation, and your notes, here are some gift
            suggestions.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/6" />
              <Skeleton className="h-4 w-full" />
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : (
            <ul className="list-disc space-y-2 pl-5 text-sm text-foreground">
              {suggestions.map((idea, index) => (
                <li key={index}>{idea}</li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
